import { Head, Link } from '@inertiajs/react'
import MainLayout from '../components/layout/MainLayout'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'

/**
 * Home page - Landing page SmartImmo
 */
export default function Home() {
  const benefits = [
    {
      title: 'Génération automatique',
      description: 'Transformez vos visiteurs en agence en leads qualifiés. Zéro effort commercial.',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Grande étincelle centrale - croix parfaite */}
          <path d="M12 2L12.5 11.5L12 12L11.5 11.5L12 2Z" fill="#3B82F6"/>
          <path d="M12 12L12.5 12.5L12 22L11.5 12.5L12 12Z" fill="#3B82F6"/>
          <path d="M2 12L11.5 12.5L12 12L11.5 11.5L2 12Z" fill="#3B82F6"/>
          <path d="M12 12L12.5 11.5L22 12L12.5 12.5L12 12Z" fill="#3B82F6"/>
          {/* Petite étincelle haut-droite */}
          <path d="M18 6L18.5 8L18 10L17.5 8L18 6Z" fill="#3B82F6"/>
          <path d="M16 8L18 8.5L20 8L18 7.5L16 8Z" fill="#3B82F6"/>
          {/* Petite étincelle bas-gauche */}
          <path d="M6 18L6.5 20L6 22L5.5 20L6 18Z" fill="#3B82F6"/>
          <path d="M4 20L6 20.5L8 20L6 19.5L4 20Z" fill="#3B82F6"/>
          {/* Petite étincelle bas-droite */}
          <path d="M18 16L18.5 17.5L18 19L17.5 17.5L18 16Z" fill="#3B82F6"/>
          <path d="M16.5 17.5L18 18L19.5 17.5L18 17L16.5 17.5Z" fill="#3B82F6"/>
        </svg>
      ),
    },
    {
      title: 'Qualification IA',
      description: 'Score de qualité instantané. Gagnez 15 minutes par lead.',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" fill="#3B82F6" fillOpacity="0.1"/>
          <path d="M12 6V12L16 14" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="10" stroke="#3B82F6" strokeWidth="2"/>
        </svg>
      ),
    },
    {
      title: 'ROI immédiat',
      description: 'Coût par lead 50% inférieur au marché. Pay-per-lead, pas d\'abonnement.',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Axes du graphique */}
          <path d="M3 21V3" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
          <path d="M3 21H21" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
          {/* Ligne de tendance montante */}
          <path d="M6 18L9 14L13 16L17 9L20 6" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          {/* Flèche en haut à droite */}
          <path d="M20 6L20 10" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
          <path d="M20 6L16 6" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
          <path d="M16 10L20 6L20 10" fill="#3B82F6"/>
        </svg>
      ),
    },
  ]

  const steps = [
    {
      number: '1',
      title: 'Affichez le QR code',
      description: 'Imprimez et placez le QR code dans votre agence'
    },
    {
      number: '2',
      title: 'Les visiteurs scannent',
      description: 'Vos visiteurs échangent avec l\'IA conversationnelle'
    },
    {
      number: '3',
      title: 'Recevez des leads qualifiés',
      description: 'Accédez aux leads qualifiés dans votre dashboard'
    }
  ]

  const stats = [
    { value: '15 000+', label: 'Agences partenaires' },
    { value: '50%', label: 'Coût inférieur au marché' },
    { value: '15 min', label: 'Gagnées par lead' },
  ]

  return (
    <MainLayout>
      <Head title="SmartImmo - L'IA qui transforme vos visiteurs en mandats">
        <meta name="description" content="Générez des leads immobiliers qualifiés automatiquement grâce à l'IA. QR code en agence, qualification instantanée, ROI garanti." />
        <meta property="og:title" content="SmartImmo - L'IA qui transforme vos visiteurs en mandats" />
        <meta property="og:description" content="Générez des leads immobiliers qualifiés automatiquement grâce à l'IA. QR code en agence, qualification instantanée, ROI garanti." />
      </Head>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="info" className="mb-4">
              Propulsé par l'Intelligence Artificielle
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              L'IA qui transforme vos<br />
              visiteurs en mandats
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Générez des leads immobiliers qualifiés automatiquement avec notre agent IA conversationnel.
              QR code en agence, qualification instantanée, ROI garanti.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/sign-up">
                <Button variant="primary" size="lg">
                  Démarrer gratuitement
                </Button>
              </Link>
              <Link href="/tarifs">
                <Button variant="outline" size="lg">
                  Voir la démo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir SmartImmo ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              La solution la plus simple et rentable pour générer des leads immobiliers qualifiés
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} hoverable>
                <Card.Body>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              En 3 étapes simples, commencez à générer des leads qualifiés
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 text-white text-2xl font-bold rounded-full mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Tarification simple et transparente
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Pay-per-lead uniquement. Pas d'abonnement caché.
          </p>

          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">35€</div>
                <div className="text-sm text-gray-600">Lead Achat</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">50€</div>
                <div className="text-sm text-gray-600">Lead Vente</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">15€</div>
                <div className="text-sm text-gray-600">Lead Location</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">25€</div>
                <div className="text-sm text-gray-600">Lead Estimation</div>
              </div>
            </div>
          </div>

          <Link href="/tarifs">
            <Button variant="outline" size="lg">
              Voir tous les tarifs
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à transformer vos visiteurs en clients ?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Rejoignez les 15 000 agences partenaires qui nous font confiance.
            Commencez dès aujourd'hui, sans engagement.
          </p>
          <Link href="/sign-up">
            <Button variant="secondary" size="lg">
              Créer mon compte gratuitement
            </Button>
          </Link>
        </div>
      </section>
    </MainLayout>
  )
}
